import { useRef } from "react";

import {
  AriaCalendarGridProps,
  AriaDateFieldProps,
  DateValue,
  RangeCalendarProps,
  useButton,
  useCalendarCell,
  useCalendarGrid,
  useDateField,
  useDateRangePicker,
  useDateSegment,
  useLocale,
  useRangeCalendar,
} from "react-aria";

import {
  type DateFieldState,
  type DateRangePickerStateOptions,
  type DateSegment,
  type RangeCalendarState,
  useDateFieldState,
  useDateRangePickerState,
  useRangeCalendarState,
} from "react-stately";

import {
  CalendarDate,
  createCalendar,
  getDayOfWeek,
  getWeeksInMonth,
  isSameDay,
} from "@internationalized/date";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { Icon } from "@mindlercare/ui-web";
import { useTranslation } from "react-i18next";
import { ChevronLeftButton, ChevronRightButton } from "./ChevronButtons";
import { PickerPopover } from "./PickerPopover";

interface CalendarGridWithState extends AriaCalendarGridProps {
  state: RangeCalendarState;
}

const DateRangePicker = (props: DateRangePickerStateOptions<CalendarDate>) => {
  const { t } = useTranslation();
  const state = useDateRangePickerState(props);
  const ref = useRef(null);
  const {
    labelProps,
    groupProps,
    startFieldProps,
    endFieldProps,
    buttonProps,
    dialogProps,
    calendarProps,
  } = useDateRangePicker(props, state, ref);

  const buttonRef = useRef(null);
  const { buttonProps: calendarButtonProps } = useButton(
    buttonProps,
    buttonRef,
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Typography
        insensitive
        variant="body-x-small"
        color="GRAY_DARK"
        {...labelProps}
      >
        {props.label}
      </Typography>
      <div style={{ display: "flex" }}>
        <DateRangePickerContainer
          isInvalid={state.isInvalid}
          {...groupProps}
          ref={ref}
        >
          <DateRangeInputContainer className="field">
            <DateField {...startFieldProps} hideTimeZone />
            <Typography
              insensitive
              variant="body-x-small"
              style={{ padding: "0 4px" }}
            >
              –
            </Typography>
            <DateField {...endFieldProps} hideTimeZone />
          </DateRangeInputContainer>

          <OpenCalendarViewButton {...calendarButtonProps}>
            <Icon.Calendar style={{ fill: defaultTheme.COLORS.BLACK_GREY }} />
          </OpenCalendarViewButton>
        </DateRangePickerContainer>
      </div>
      {state.isInvalid && (
        <Typography
          insensitive
          variant="body-small"
          color="ERROR"
          aria-hidden="true"
        >
          {t("INVALID_DATE_RANGE")}
        </Typography>
      )}

      {state.isOpen && (
        <PickerPopover
          state={state}
          triggerRef={ref}
          placement="bottom start"
          offset={8}
          {...dialogProps}
        >
          <RangeCalendar {...calendarProps} />
        </PickerPopover>
      )}
    </div>
  );
};

const DateField = (props: AriaDateFieldProps<DateValue>) => {
  const { locale } = useLocale();
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef(null);
  const { labelProps, fieldProps } = useDateField(props, state, ref);

  return (
    <div className="wrapper" style={{ display: "flex" }}>
      <Typography insensitive variant="body-x-small" {...labelProps}>
        {props.label}
      </Typography>
      <div
        {...fieldProps}
        ref={ref}
        className="field"
        style={{ display: "flex" }}
      >
        {state.segments.map((segment, i) => (
          <DateSegment key={i} segment={segment} state={state} />
        ))}
      </div>
    </div>
  );
};

const DateSegment = ({
  segment,
  state,
}: {
  segment: DateSegment;
  state: DateFieldState;
}) => {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  return (
    <div
      {...segmentProps}
      ref={ref}
      className={`segment ${segment.isPlaceholder ? "placeholder" : ""}`}
    >
      <Typography insensitive variant="body">
        {segment.text}
      </Typography>
    </div>
  );
};

const RangeCalendar = (props: RangeCalendarProps<DateValue>) => {
  const { locale } = useLocale();
  const state = useRangeCalendarState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef(null);
  const { calendarProps, prevButtonProps, nextButtonProps, title } =
    useRangeCalendar(props, state, ref);

  return (
    <RangeCalendarContainer {...calendarProps} ref={ref} className="calendar">
      <RangeCalendarTitleRow className="header">
        <ChevronLeftButton {...prevButtonProps} />
        <Typography
          insensitive
          variant="h3"
          style={{ textTransform: "capitalize" }}
        >
          {title}
        </Typography>
        <ChevronRightButton {...nextButtonProps} />
      </RangeCalendarTitleRow>
      <CalendarGrid state={state} />
    </RangeCalendarContainer>
  );
};

const CalendarGrid = ({ state, ...props }: CalendarGridWithState) => {
  const { locale } = useLocale();
  const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);

  // Get the number of weeks in the month so we can render the proper number of rows.
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <CalendarGridTable {...gridProps} cellPadding={0}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            <CalendarDayHeader key={index}>{day}</CalendarDayHeader>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex)
              .map((date, i) =>
                date ? (
                  <CalendarCell key={i} state={state} date={date} />
                ) : (
                  <td key={i} />
                ),
              )}
          </tr>
        ))}
      </tbody>
    </CalendarGridTable>
  );
};

const CalendarCell = ({
  state,
  date,
}: {
  state: RangeCalendarState;
  date: CalendarDate;
}) => {
  const ref = useRef(null);
  const { locale } = useLocale();
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);
  const dayOfWeek = getDayOfWeek(date, locale);

  const isSelectionStart = state.highlightedRange
    ? isSameDay(date, state.highlightedRange.start)
    : isSelected;
  const isSelectionEnd = state.highlightedRange
    ? isSameDay(date, state.highlightedRange.end)
    : isSelected;
  const isRoundedLeft =
    isSelected && (isSelectionStart || dayOfWeek === 0 || date.day === 1);

  const isRoundedRight =
    isSelected &&
    (isSelectionEnd ||
      dayOfWeek === 6 ||
      date.day === date.calendar.getDaysInMonth(date));

  const isFullyRounded = isSelectionStart && isSelectionEnd;

  return (
    <td {...cellProps} style={{ padding: 0 }}>
      <DayCell
        {...buttonProps}
        ref={ref}
        hidden={isOutsideVisibleRange}
        isSelected={isSelected}
        isRoundedLeft={isRoundedLeft}
        isRoundedRight={isRoundedRight}
        isFullyRounded={isFullyRounded}
        isStartOrEnd={isSelectionStart || isSelectionEnd}
        className={`cell ${isSelected ? "selected" : ""} ${
          isDisabled ? "disabled" : ""
        } ${isUnavailable ? "unavailable" : ""}`}
      >
        {formattedDate}
      </DayCell>
    </td>
  );
};

const OpenCalendarViewButton = styled.button`
  border: none;
  border-left: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
  background-color: transparent;
  padding: 0 16px 0 12px;
  height: 100%;
  border-radius: 0 70px 70px 0;
  outline: none;
`;

const DateRangePickerContainer = styled.div<{ isInvalid: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  min-width: 200px;
  padding: 0;
  background: ${({ theme }) => theme.COLORS.WHITE};
  border: 1px solid
    ${({ theme, isInvalid }) =>
      isInvalid ? theme.COLORS.ERROR : theme.COLORS.GRAY_MEDIUM};
  box-sizing: border-box;

  border-radius: 70px;
`;

const DateRangeInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
  flex-direction: row;
  align-items: center;
  padding: 8px 0 8px 16px;
`;

const RangeCalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.PADDING.MD};
`;

const RangeCalendarTitleRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.PADDING.MD};
  align-items: center;
  justify-content: space-between;
  min-width: 240px;
`;

const CalendarDayHeader = styled.th`
  aspect-ratio: 1 / 1;
`;

const CalendarGridTable = styled.table`
  width: 200px;
  border-spacing: 0 ${({ theme }) => theme.PADDING.SM};
`;

const DayCell = styled.div<{
  isSelected: boolean;
  isStartOrEnd: boolean;
  isRoundedLeft: boolean;
  isRoundedRight: boolean;
  isFullyRounded: boolean;
}>`
  display: ${(props) => (props.hidden ? "none" : "flex")};
  justify-content: center;
  align-items: center;
  aspect-ratio: 1 / 1;
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL_MEDIUM};
  padding: ${({ theme }) => theme.PADDING.SM};
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.COLORS.PRIMARY : "none"};
  background-color: ${({ theme, isStartOrEnd }) =>
    isStartOrEnd ? theme.COLORS.PRIMARY_DARK : "none"};
  font-weight: ${({ theme, isStartOrEnd }) =>
    isStartOrEnd ? theme.FONT.WEIGHT.MEDIUM : theme.FONT.WEIGHT.REGULAR};
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
  outline: none;
  border-radius: ${({ isRoundedLeft, isRoundedRight, isFullyRounded }) => {
    if (isFullyRounded) {
      return "100%";
    }
    if (isRoundedRight) {
      return "0 100% 100% 0";
    }
    if (isRoundedLeft) {
      return "100% 0 0 100%";
    }
    return 0;
  }};
`;

export default DateRangePicker;
